/**
 * FeedbackButtons — inline 👍 / 👎 row attached to each assistant message.
 *
 * On 👎, expands a compact comment textarea inline.  Submission fires a
 * non-blocking POST /feedback with the full message context attached so
 * the admin dashboard can reconstruct the bug without joining sessions.
 *
 * UX rules:
 *   - One submission per message; pressing again is a no-op (visually
 *     dimmed once submitted).
 *   - Errors are silently swallowed — feedback failure must never block
 *     the chat experience.
 *   - 👍 submits immediately.  👎 expands the comment textarea; user can
 *     submit with or without text.
 */
import { useState } from "react";
import { submitFeedback, type Rating } from "../lib/feedback";

interface FeedbackButtonsProps {
  sessionId: string;
  messageId: string;
  question: string;
  responseText: string;
  intent?: string;
  cardType?: string;
  pokemonNames?: string[];
}

export default function FeedbackButtons(props: FeedbackButtonsProps) {
  const [submitted, setSubmitted] = useState<Rating | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);

  const send = async (rating: Rating, includeComment = false) => {
    if (submitted) return;
    setSending(true);
    try {
      await submitFeedback({
        session_id: props.sessionId,
        message_id: props.messageId,
        rating,
        comment: includeComment ? comment : undefined,
        question: props.question,
        response_text: props.responseText,
        intent: props.intent,
        card_type: props.cardType,
        pokemon_names: props.pokemonNames,
      });
      setSubmitted(rating);
      setExpanded(false);
    } catch (err) {
      // Silent — don't block the chat over a feedback failure.
      console.warn("feedback submit failed:", err);
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-white/35 mt-1 select-none">
        <span aria-hidden="true">{submitted === "up" ? "👍" : "👎"}</span>
        <span>Thanks — feedback recorded</span>
      </div>
    );
  }

  return (
    <div className="mt-1 -ml-1">
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          aria-label="Helpful response"
          onClick={() => void send("up")}
          disabled={sending}
          className="px-2 py-1 rounded-md text-white/30 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors disabled:opacity-40"
        >
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 11v9a1 1 0 0 0 1 1h7.5a2 2 0 0 0 1.96-1.6L19 13h-5l1.5-5.5a1.5 1.5 0 0 0-2.85-.93L9.5 11H7Zm0 0H4v10h3" />
          </svg>
        </button>
        <button
          type="button"
          aria-label="Not helpful"
          onClick={() => setExpanded((e) => !e)}
          disabled={sending}
          className={`px-2 py-1 rounded-md transition-colors disabled:opacity-40 ${
            expanded
              ? "text-red-400 bg-red-500/10"
              : "text-white/30 hover:text-red-400 hover:bg-red-500/10"
          }`}
        >
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 13V4a1 1 0 0 0-1-1H8.5a2 2 0 0 0-1.96 1.6L5 11h5l-1.5 5.5a1.5 1.5 0 0 0 2.85.93L14.5 13H17Zm0 0h3V3h-3" />
          </svg>
        </button>
      </div>

      {expanded && (
        <div className="mt-2 flex flex-col gap-2 max-w-md">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 500))}
            placeholder="What went wrong? (optional)"
            maxLength={500}
            rows={2}
            className="w-full text-sm bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-white/90 placeholder-white/30 outline-none focus:border-white/20 transition-colors resize-none"
            autoFocus
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void send("down", true)}
              disabled={sending}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send feedback"}
            </button>
            <button
              type="button"
              onClick={() => { setExpanded(false); setComment(""); }}
              disabled={sending}
              className="px-3 py-1.5 rounded-lg text-xs text-white/40 hover:text-white/70 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <span className="ml-auto text-[10px] text-white/25">{comment.length}/500</span>
          </div>
        </div>
      )}
    </div>
  );
}
